import React, { MutableRefObject } from 'react'

import { useForwardedRef } from 'utils/commonHooks'

import { EditorInputProps, InputElement } from './types'

const EditorInput: React.ForwardRefRenderFunction<InputElement, EditorInputProps> = (
  { contentRef, multiline, noWrap, rows, selection, onChange, onSelect, ...props },
  forwardedRef,
) => {
  const [elementRef, elementRefHandler] = useForwardedRef<HTMLTextAreaElement>(
    forwardedRef as MutableRefObject<HTMLTextAreaElement>,
  )
  const scrollLeftRef = React.useRef<number>(0)

  const checkScrollLeft = () => {
    // Browser updates scrollLeft after event handlers, using setTimeout to get actual value
    setTimeout(() => {
      const element = elementRef.current
      const contentElement = contentRef.current
      if (!element || !contentElement) {
        return
      }

      if (scrollLeftRef.current !== element.scrollLeft) {
        scrollLeftRef.current = element.scrollLeft
        contentElement.scrollLeft = element.scrollLeft
      }
    })
  }

  React.useLayoutEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    if (selection.start !== element.selectionStart || selection.end !== element.selectionEnd) {
      element.setSelectionRange(
        selection.start,
        selection.end,
        element.selectionDirection || undefined,
      )
    }

    if (multiline) {
      setSizeToScrollSize(element, 'height')
      if (noWrap) {
        element.style.minWidth = '0px'
        setSizeToScrollSize(element, 'width')
        element.style.minWidth = ''
      }
    } else {
      element.style.width = ''
      element.style.width = `${element.scrollWidth}px`
      checkScrollLeft()
    }
  })

  const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const input = event.currentTarget
    if (input.selectionStart === null || input.selectionEnd === null) {
      return
    }
    onSelect(input.selectionStart, input.selectionEnd)
  }

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const input = event.target
    onChange(input.value, input.selectionStart || 0)
  }

  return (
    <textarea
      ref={elementRefHandler}
      {...props}
      onChange={handleChange}
      onSelect={handleSelect}
      rows={multiline ? rows : 1}
      data-mc-editor="true"
    />
  )
}

export default React.forwardRef(EditorInput)

export function setSizeToScrollSize(element: HTMLElement, dimension: 'width' | 'height') {
  const isWidth = dimension === 'width'

  const parent = element.parentElement
  const parentSize = parent?.style[dimension] ?? ''
  if (parent) {
    parent.style[dimension] = element.style[dimension]
  }

  element.style[dimension] = ''
  element.style[dimension] = `${isWidth ? element.scrollWidth : element.scrollHeight}px`

  if (parent) {
    parent.style[dimension] = parentSize
  }
}
